import React, { useState } from 'react';
import { Activity, Zap, TrendingUp, Wifi, WifiOff, Table, LineChart } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import { useOptionChainStore } from '../../store/optionChainStore';
import ArbitrageView from './components/ArbitrageView';
import SyncArbitrageView from './components/SyncArbitrageView';
import StockScreenerView from './components/StockScreenerView';
import CommodityTrackerView from './components/CommodityTracker/CommodityTrackerView';

const OpportunityScreener = () => {
    const status = useOptionChainStore(state => state.status);
    const [viewMode, setViewMode] = useState("arbitrage"); // 'arbitrage' | 'sync-arbitrage' | 'screener'
    const [activeTab, setActiveTab] = useState("GOLD");

    return (
        <DashboardLayout>
            <div className="flex flex-col h-full w-full bg-[#0f172a] text-slate-200 font-inter selection:bg-indigo-500/30">
                {/* App Header */}
                <header className="flex-none px-6 py-4 flex justify-between items-center border-b border-slate-700/50 bg-[#0f172a]/95 backdrop-blur z-40">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-2 rounded-lg shadow-lg shadow-indigo-500/20">
                            <TrendingUp className="text-white w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-100 tracking-tight">Opportunity Screener</h1>
                            <p className="text-xs text-slate-500 font-medium">Global Volatility Monitor</p>
                        </div>
                    </div>

                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${status === 'connected'
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                        : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                        } `}>
                        {status === 'connected' ? <Wifi size={14} /> : <WifiOff size={14} />}
                        <span className="text-xs font-bold tracking-wide">{status.toUpperCase()}</span>
                    </div>
                </header>

                {/* Section Tabs */}
                <div className="flex-none px-6 border-b border-slate-700/50 bg-[#0f172a]/95 backdrop-blur z-30">
                    <div className="flex items-center gap-8">
                        {/* VIEW MODE TABS */}
                        <div className="flex gap-4 py-4 border-r border-slate-700/50 pr-8">
                            <button
                                onClick={() => setViewMode("arbitrage")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'arbitrage' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Zap size={16} /> Arbitrage
                            </button>
                            <button
                                onClick={() => setViewMode("sync-arbitrage")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'sync-arbitrage' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Activity size={16} /> Sync Arb
                            </button>
                            <button
                                onClick={() => setViewMode("screener")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'screener' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Table size={16} /> Stock Screener
                            </button>
                            <button
                                onClick={() => setViewMode("commodity-tracker")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'commodity-tracker' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <LineChart size={16} /> Commodity Tracker
                            </button>
                        </div>

                        {/* SUB TABS (Visible for Arbitrage & Sync Arb) */}
                        {(viewMode === 'arbitrage' || viewMode === 'sync-arbitrage') && (
                            <div className="flex gap-1">
                                {['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`relative px-4 py-4 text-xs font-bold uppercase tracking-wider transition-colors outline-none
                                            ${activeTab === tab
                                                ? 'text-indigo-400'
                                                : 'text-slate-500 hover:text-slate-300'
                                            }`}
                                    >
                                        {tab}
                                        {activeTab === tab && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]"></div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <main className="flex-1 p-6 overflow-auto custom-scrollbar relative">
                    {viewMode === 'arbitrage' && (
                        <ArbitrageView
                            activeCommodityTab={activeTab}
                            setActiveCommodityTab={setActiveTab}
                        />
                    )}
                    {viewMode === 'sync-arbitrage' && (
                        <SyncArbitrageView
                            activeCommodityTab={activeTab}
                            setActiveCommodityTab={setActiveTab}
                        />
                    )}
                    {viewMode === 'screener' && <StockScreenerView />}
                    {viewMode === 'commodity-tracker' && <CommodityTrackerView />}
                </main>
            </div>
        </DashboardLayout>
    );
};

export default OpportunityScreener;
